import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import {
  useGetActiveHeroConversations,
  useGetAssociateEvents,
  useReadAssociateEvent,
  VsAssociateEventType,
} from '@vs-online-only-app/state';

const MAX_MISSED_CHATS = 3;

enum DisplayReason {
  MAX_MISSED = 'max-missed',
  MAX_DECLINED = 'max-declined',
}

const AssociateSetToUnavailableModal = () => {
  const { data: associateEvents } = useGetAssociateEvents();
  const [displayReason, setDisplayReason] = useState<DisplayReason>();
  const { data: activeConversations } = useGetActiveHeroConversations();
  const hasActiveConversations = Boolean(activeConversations?.length);
  const readAssociateEventMutation = useReadAssociateEvent();

  const setToUnavailableEvent = useMemo(
    () =>
      associateEvents?.find((event) => {
        if (event.read) return false;

        if (
          event.type === VsAssociateEventType.ConversationDeclined &&
          event.data.maxDecline
        ) {
          return true;
        }

        if (
          event.type === VsAssociateEventType.ConversationMissed &&
          event.data.missed >= MAX_MISSED_CHATS
        ) {
          return true;
        }

        return false;
      }),
    [associateEvents],
  );

  useEffect(() => {
    if (!setToUnavailableEvent) return;
    setDisplayReason(
      setToUnavailableEvent.type === VsAssociateEventType.ConversationDeclined
        ? DisplayReason.MAX_DECLINED
        : DisplayReason.MAX_MISSED,
    );
  }, [setToUnavailableEvent]);

  const message = useMemo(() => {
    if (hasActiveConversations) {
      return "You can continue chatting with customers in your active chats. When you're ready to accept more chats, go online again.";
    }
    return "When you're ready to accept more chats, go online again.";
  }, [hasActiveConversations]);

  const onClose = () => {
    if (setToUnavailableEvent) {
      readAssociateEventMutation.mutate(setToUnavailableEvent.id);
    }
    setDisplayReason(undefined);
  };

  return (
    <Dialog open={Boolean(displayReason)} onClose={onClose}>
      <DialogTitle>We set you to unavailable.</DialogTitle>
      <Divider />
      <DialogContent>
        <Stack>
          <Typography>
            This is because you{' '}
            {displayReason === DisplayReason.MAX_DECLINED
              ? 'declined'
              : 'missed'}{' '}
            {MAX_MISSED_CHATS} chats in a row.
          </Typography>
          <Typography>{message}</Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>OK</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssociateSetToUnavailableModal;
